import React from 'react';
import { func, node, object, shape, bool, string } from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { PriceWithSchema } from '@ynap/price';
import { SingleBadge } from '@ynap/badge';
import { CombinedImage } from '@ynap/image';
import ProductInformation from '../../ProductInformation/ProductInformation';
import ImageCarousel from '../../ImageCarousel/ImageCarousel';
import { ProductDetailsSkeletonBasicInfo, ProductDetailsControls } from '../../ProductDetailsSkeleton/ProductDetailsSkeleton';

const bem = new BEMHelper('LiteProductDetailsPage');

const generateImageSrcsetSize = () => '(min-width: 1024px) 100vw, (min-width: 768px) 40vw, 75vw';

const LiteProductDetailsPage = (props) => {
    const {
        product = {},
        selectedProductVariant = {},
        consideredBadge,
        messages,
        children,
        loading,
        productImageAspectRatio,
        locale,
        displayBadges = false,
        hidePrice = false,
        withQuickAdd = false,
        config,
        isWishlist,
        country,
        isShoppingBag,
        ...rest
    } = props;
    const language = locale.split('-')[0];

    const { name, designerName } = product;

    const { price, images, badges, videos, colourPartNumber, saleExclusive } = selectedProductVariant;
    const minimumPriceCountries = config?.head?.minimumPriceCountries;
    const countryFromLocale = locale?.split('-')[1]?.toLocaleUpperCase();
    const showMinimumPriceLabel = minimumPriceCountries?.includes(countryFromLocale) && typeof price?.minimumPrice === 'number';
    const isEUcountry = minimumPriceCountries?.includes(countryFromLocale);

    const imageSrcsetSize = generateImageSrcsetSize();
    const modifiers = { quickAdd: withQuickAdd, consideredBadge };
    const image = withQuickAdd ? images?.find((image) => image.id === 'in') || images?.[0] : null;
    const selectedProductBadgeModifier = badges?.selectedProductColorFirstBadge?.key ? 'selectedProductBadgeModifier' : null;

    return (
        <div className={bem('layoutGrid', modifiers)}>
            {withQuickAdd ? (
                <div className={bem('imageCarousel', { quickAdd: withQuickAdd })}>
                    <CombinedImage
                        src={image?.url}
                        alt={image?.alt}
                        loading={loading}
                        yOffset={'0%'}
                        aspectRatio={productImageAspectRatio}
                        imageSrcsetSize={imageSrcsetSize}
                        primarySrcset={image?.srcSet}
                        displayError={!(loading || image?.url)}
                        withIntersectionObserver={true}
                        withSkeleton={true}
                        hideSecondaryOnMobile={true}
                    />
                </div>
            ) : (
                <ImageCarousel
                    className={bem('imageCarousel')}
                    images={images}
                    paginationStacked={true}
                    loading={loading}
                    productImageAspectRatio={productImageAspectRatio}
                    imageSrcsetSize={imageSrcsetSize}
                    videos={videos}
                    allow1stLevelZoom={false}
                    allow2ndLevelZoom={false}
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                    {...rest}
                    NAPImageCarouselModifier={'layoutDefaultView'}
                />
            )}
            <div className={bem('productDetailsWrapper', modifiers)}>
                {loading ? (
                    <div className={bem('productDetails', modifiers)}>
                        <ProductDetailsSkeletonBasicInfo className={bem('productInformation', modifiers)} />
                        <ProductDetailsControls className={bem('children', modifiers)} withQuickAdd={withQuickAdd} />
                    </div>
                ) : (
                    <div className={bem('productDetails', modifiers)}>
                        <ProductInformation
                            className={{
                                container: bem('productInformation', modifiers),
                                selectedProductBadgeModifier: selectedProductBadgeModifier,
                            }}
                            productId={colourPartNumber}
                            name={name}
                            designerName={designerName}
                            locale={locale}
                        />
                        {displayBadges && badges && badges.selectedProductColorFirstBadge && (
                            <SingleBadge className={bem('productColourBadge', modifiers)} badge={badges.selectedProductColorFirstBadge} />
                        )}
                        {consideredBadge && (
                            <SingleBadge
                                className={bem('consideredBadge')}
                                considered={true}
                                badge={{ label: consideredBadge, key: 'CONSIDERED_BADGE' }}
                                translation={messages.considered.badge()}
                            />
                        )}
                        {!hidePrice && (
                            <PriceWithSchema
                                className={bem('price', modifiers)}
                                details
                                {...price}
                                messages={messages}
                                locale={locale}
                                priceSuffix={messages.price?.taxIncluded?.()}
                                showMinimumPriceLabel={showMinimumPriceLabel}
                                isWishlist={isWishlist}
                                isEUcountry={isEUcountry}
                                designerName={designerName}
                                badgeKey={selectedProductVariant?.badges?.selectedProductColorFirstBadge?.key}
                                saleExclusive={saleExclusive}
                                country={country}
                                isShoppingBag={isShoppingBag}
                                addMicrodata={true}
                            />
                        )}
                        <div className={bem('children')}>{children}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

LiteProductDetailsPage.propTypes = {
    product: object.isRequired,
    selectedProductVariant: object.isRequired,
    messages: shape({
        colourAndSize: shape({
            colour: func.isRequired,
        }).isRequired,
        product: shape({
            productCode: func.isRequired,
            viewMoreDetails: func.isRequired,
        }).isRequired,
    }),
    children: node,
    displayBadges: bool,
    loading: bool,
    locale: string,
    productImageAspectRatio: string,
    consideredBadge: string,
    hidePrice: bool,
    withQuickAdd: bool,
    config: object,
    isWishlist: bool,
};

export default LiteProductDetailsPage;
